// oxlint-disable-next-line no-restricted-imports
import { Group } from "@mantine/core";
import { IconX } from "@tabler/icons-react";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { shadows } from "#app/theme/v2/shadows";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Divider } from "#components/atoms/Divider/Divider";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Tooltip } from "#components/atoms/Tooltip/Tooltip";
import { Text } from "#components/atoms/Typographies/Text";

import type { ReactNode } from "react";

type BulkActionBarActionCommonProps = {
  variant?: "primary" | "secondary";
  key: string;
  icon: ReactNode;
  onClick: () => void;
};

export type BulkActionBarActionDisabledProps =
  | {
      disabled: true;
      disabledTooltip: ReactNode;
    }
  | {
      disabled?: false;
      disabledTooltip?: undefined;
    };

type BulkActionBarActionLabelProps =
  | {
      label: string;
      ariaLabel?: undefined;
    }
  | {
      ariaLabel: string;
      label?: undefined;
    };

type BulkActionBarAction = BulkActionBarActionCommonProps &
  BulkActionBarActionDisabledProps &
  BulkActionBarActionLabelProps;

type BulkActionBarProps = {
  label: ReactNode;
  clearAriaLabel: string;
  onClear: () => void;
  actions: BulkActionBarAction[];
  children?: ReactNode;
};

const withDisabledTooltip = (action: BulkActionBarAction, control: ReactNode) =>
  action.disabled ? (
    <Tooltip key={action.key} label={action.disabledTooltip}>
      <span>{control}</span>
    </Tooltip>
  ) : (
    control
  );

const BulkActionBar = ({
  label,
  clearAriaLabel,
  onClear,
  actions,
  children,
}: BulkActionBarProps) => {
  return (
    <Group
      wrap="nowrap"
      align="center"
      gap={spacing[3]}
      pl={spacing[6]}
      pr={spacing[3]}
      py={spacing[3]}
      style={{
        background: colors.bg.elevation.primary,
        borderRadius: radius.xxxl,
        boxShadow: shadows.elevation3,
        width: "fit-content",
      }}
    >
      <Group wrap="nowrap" align="center" gap={spacing[2]}>
        <Text size="small" type="regular">
          {label}
        </Text>
        <IconButton variant="tertiary" size="sm" aria-label={clearAriaLabel} onClick={onClear}>
          <IconX size={16} />
        </IconButton>
      </Group>
      <Divider type="secondary" orientation="vertical" style={{ height: spacing[8] }} />
      {children}
      <Group wrap="nowrap" align="center" gap={spacing[2]}>
        {actions.map((action) =>
          withDisabledTooltip(
            action,
            action.label !== undefined ? (
              <Button
                key={action.key}
                variant={action.variant ?? "secondary"}
                size="md"
                leftSection={action.icon}
                disabled={action.disabled}
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            ) : (
              <IconButton
                key={action.key}
                variant={action.variant ?? "secondary"}
                size="md"
                aria-label={action.ariaLabel}
                disabled={action.disabled}
                onClick={action.onClick}
              >
                {action.icon}
              </IconButton>
            ),
          ),
        )}
      </Group>
    </Group>
  );
};

export { BulkActionBar };
